import { useEffect, useRef } from 'react'
import { loadGoogleGsi } from '@/utils/googleGsi'

/**
 * Renders Google's official Sign-In button (Google Identity Services). On a
 * successful sign-in Google hands back a JWT `credential` (id_token) which we
 * forward to `onCredential` — the caller POSTs it to /api/auth/google/.
 *
 * The official rendered button is used (rather than a custom-styled one)
 * because it is the reliable, branding-compliant way to obtain an id_token.
 */
export default function GoogleSignInButton({ clientId, onCredential, onError }) {
  const containerRef = useRef(null)
  // Keep the latest callback without re-initialising GIS on every render.
  const onCredentialRef = useRef(onCredential)
  onCredentialRef.current = onCredential

  useEffect(() => {
    if (!clientId) return
    let cancelled = false

    loadGoogleGsi()
      .then(() => {
        if (cancelled || !containerRef.current) return
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response?.credential) onCredentialRef.current?.(response.credential)
          },
        })
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: containerRef.current.offsetWidth || 320,
        })
      })
      .catch((err) => { if (!cancelled) onError?.(err) })

    return () => { cancelled = true }
  }, [clientId, onError])

  return <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center', minHeight: 40 }} />
}
